import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { catchError, map, of, switchMap, timeout } from 'rxjs';

import { AuthService } from './auth.service';
import { AuthStore } from './auth.store';
import { BootstrapStore } from './bootstrap.store';
import { UserType } from './user.interface';

const resolveAccessGuard = (roles: UserType[] | undefined, permissions: string[] | undefined) => {
    const authStore = inject(AuthStore);
    const authService = inject(AuthService);
    const bootstrapStore = inject(BootstrapStore);
    const router = inject(Router);

    const hasRoleAccess = () => {
        if (!roles?.length) {
            return true;
        }

        const userType = authStore.user()?.type;
        return userType ? roles.includes(userType) : false;
    };

    const hasPermissionAccess = () => {
        if (!permissions?.length) {
            return true;
        }

        return bootstrapStore.hasPermission(permissions) || authStore.can(permissions);
    };

    const hasAccess = () => hasRoleAccess() && hasPermissionAccess();

    const ensureBootstrap = () => {
        if (bootstrapStore.loaded()) {
            return of(true);
        }

        return bootstrapStore.load().pipe(map(() => true));
    };

    if (authStore.isAuthenticated()) {
        return ensureBootstrap().pipe(
            map(() => (hasAccess() ? true : router.createUrlTree(['/notfound']))),
            catchError(() => of(hasAccess() ? true : router.createUrlTree(['/notfound'])))
        );
    }

    if (!authStore.token()) {
        return router.createUrlTree(['/auth/login']);
    }

    return authService.me().pipe(
        timeout({ each: 8000 }),
        switchMap(() => ensureBootstrap()),
        map(() => (hasAccess() ? true : router.createUrlTree(['/notfound']))),
        catchError(() => of(router.createUrlTree(['/auth/login'])))
    );
};

export const authGuard: CanActivateFn = (route) => resolveAccessGuard(route.data?.['roles'], route.data?.['permissions']);

export const authChildGuard: CanActivateChildFn = (route) => resolveAccessGuard(route.data?.['roles'], route.data?.['permissions']);
